// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';

export interface PerfOptions {
  fps?: boolean;
  longTask?: boolean;
  paint?: boolean;
  logToConsole?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PerfMonitorService {
  private fpsHandle: number | null = null;
  private lastFrame = 0;
  private frames = 0;
  private poLongTask?: PerformanceObserver;
  private poPaint?: PerformanceObserver;
  private options: PerfOptions = {};

  start(opts: PerfOptions = {}): void {
    this.options = { fps: true, longTask: true, paint: false, logToConsole: false, ...opts };

    if (this.options.fps) this.startFps();
    if (this.options.longTask) this.startLongTask();
    if (this.options.paint) this.startPaint();
  }

  stop(): void {
    if (this.fpsHandle) {
      cancelAnimationFrame(this.fpsHandle);
      this.fpsHandle = null;
    }
    try { this.poLongTask?.disconnect(); } catch {}
    try { this.poPaint?.disconnect(); } catch {}
  }

  private startFps() {
    const loop = (t: number) => {
      if (!this.lastFrame) this.lastFrame = t;
      this.frames++;
      if (t - this.lastFrame >= 1000) {
        const fps = this.frames;
        this.frames = 0;
        this.lastFrame = t;
        if (this.options.logToConsole) {
          console.info(`[perf] FPS: ${fps}`);
        }
      }
      this.fpsHandle = requestAnimationFrame(loop);
    };
    this.fpsHandle = requestAnimationFrame(loop);
  }

  private startLongTask() {
    try {
      this.poLongTask = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          // Long Task は 50ms 超のメインスレッドブロック
          if (this.options.logToConsole) {
            console.warn(`[perf] LongTask ${Math.round(e.duration)}ms`, e);
          }
        }
      });
      this.poLongTask.observe({ type: 'longtask', buffered: true } as any);
    } catch { /* Safari など非対応 */ }
  }

  private startPaint() {
    try {
      this.poPaint = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          if (this.options.logToConsole) {
            console.info(`[perf] Paint: ${e.name} ${Math.round(e.startTime)}ms`);
          }
        }
      });
      this.poPaint.observe({ type: 'paint', buffered: true } as any);
    } catch {}
  }
}
